import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {Progress} from '~/components/Progress';

interface IResourceResult<T> {
    isPending: boolean;
    resource: T;
    setResource: (resource: T) => void;
    fetch: () => void;
    error?: string;
}

export function useResource<T>(loader: () => Promise<T>, initialResource: T = null): IResourceResult<T> {
    const [state, setState] = useState<{ isPending: boolean; resource: T }>({isPending: true, resource: initialResource});
    const fetcher = () => {
        setState({isPending: true, resource: initialResource});
        loader().then(resource => setState({isPending: false, resource}));
    };
    useEffect(() => {
        fetcher();
    }, []);
    return {...state, setResource: (resource: T) => setState({...state, resource}), fetch: fetcher};
}

// fetches resources only by calling fetch
export function controllableUseResource<T>(loader: () => Promise<T>, initialResource: T = null): IResourceResult<T> {
    const [state, setState] = useState<{ isPending: boolean; resource: T; error?: string }>({isPending: false, resource: initialResource});
    const latest = useRef(0);
    const fetcher = () => {
        const generation = ++latest.current;
        setState({isPending: true, resource: initialResource, error: undefined});
        loader().then(resource => {
            if (generation === latest.current) {
                setState({isPending: false, resource, error: undefined});
            }
        }).catch((e) => {
            if (generation === latest.current) {
                setState({isPending: false, resource: initialResource, error: e.message});
            }
        });
    };
    return {...state, setResource: (resource: T) => setState(prev => ({...prev, resource})), fetch: fetcher};
}

export function useQueryResource<T>(loader: () => Promise<T>, deps: any[], initialResource: T = null): IResourceResult<T> {
    const [state, setState] = useState<{ isPending: boolean; resource: T; error?: string }>({isPending: true, resource: initialResource});

    const fetcher = () => {
        setState(prev => ({...prev, isPending: true, error: undefined}));
        loader()
            .then(resource => setState({isPending: false, resource, error: undefined}))
            .catch((e) => setState({isPending: false, resource: initialResource, error: e.message}));
    };

    useEffect(() => {
        fetcher();
    }, deps);

    return {...state, setResource: (resource: T) => setState({...state, resource}), fetch: fetcher};
}

interface IResourceProps<T> {
    result?: IResourceResult<T>;
    loader?: () => Promise<T>;
    message?: ReactNode;
    children: (result: IResourceResult<T>) => ReactNode;
}

export function ResourceLoader<T>(props: IResourceProps<T>) {
    let result = props.result;
    if (props.loader) {
        result = useResource(props.loader);
    }
    return result.isPending ? <Progress message={props.message}/> : <>{props.children(result)}</>;
}
